/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import React, { type FC, useCallback, useMemo } from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import { css } from '@compiled/react';

import AvatarGroup from '@atlaskit/avatar-group/avatar-group';
import Avatar from '@atlaskit/avatar/avatar';
import type { AvatarGroupOverrides, AvatarProps } from '@atlaskit/avatar-group/types';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

type AvatarGroupItemRender = NonNullable<
	NonNullable<AvatarGroupOverrides['AvatarGroupItem']>['render']
>;
type AvatarGroupItemRenderParams = Parameters<AvatarGroupItemRender>;

const styles = cssMap({
	avatarGroupRow: {
		marginTop: token('space.100'),
		marginBottom: token('space.200'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		alignItems: 'center',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
	},
	overflowItem: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingTop: token('space.075'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		cursor: 'default',
	},
	overflowItemName: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	clickableAvatar: {
		cursor: 'pointer',
	},
	clickableOverflowItem: {
		cursor: 'pointer',
	},
});

const overflowProfileCardAnchorStyles = css({
	display: 'block',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Keep the selected profile card anchored while its overflow row scrolls out of view.
	'&:has([aria-expanded="true"])': {
		position: 'sticky',
		top: 0,
		bottom: 0,
		zIndex: 1,
		backgroundColor: token('elevation.surface.overlay'),
	},
});

type AvatarRowMessages = {
	singleUserMessage: MessageDescriptor;
	multipleUsersMessage: MessageDescriptor;
};

type AvatarRowProps = {
	approverData: Array<{ name: string; src?: string; email?: string; userId?: string }>;
	messages: AvatarRowMessages;
	maxCount?: number;
	showProfileCards?: boolean;
};

const ProfileCardAvatar: FC<{ avatar: AvatarProps }> = ({ avatar }) => (
	<span data-testid="profile-card-avatar" css={styles.clickableAvatar}>
		<Avatar {...avatar} />
	</span>
);

export const AvatarRow: FC<AvatarRowProps> = ({
	approverData,
	messages,
	maxCount,
	showProfileCards = false,
}) => {
	const renderAvatar = useCallback((avatar: AvatarProps & { userId?: string }) => {
		if (!avatar.userId) {
			return <Avatar {...avatar} />;
		}

		return (
			<ProfileCardWrapper userId={avatar.userId} fullName={avatar.name} trigger="click">
				<ProfileCardAvatar avatar={avatar} />
			</ProfileCardWrapper>
		);
	}, []);

	const renderOverflowItem = useCallback<AvatarGroupItemRender>(
		(
			_Component: AvatarGroupItemRenderParams[0],
			itemProps: AvatarGroupItemRenderParams[1],
			index: AvatarGroupItemRenderParams[2],
		) => {
			const avatar = itemProps.avatar as AvatarProps & { userId?: string };
			const row = (
				<Box xcss={cx(styles.overflowItem, Boolean(avatar.userId) && styles.clickableOverflowItem)}>
					<Avatar {...avatar} size="small" />
					<Text size="small">
						<Box as="span" xcss={styles.overflowItemName}>
							{avatar.name}
						</Box>
					</Text>
				</Box>
			);

			if (!avatar.userId || !showProfileCards) {
				return <React.Fragment key={`overflow-item-${index}`}>{row}</React.Fragment>;
			}

			return (
				<div
					key={`overflow-item-${index}`}
					data-testid="overflow-profile-card-anchor"
					css={overflowProfileCardAnchorStyles}
				>
					<ProfileCardWrapper
						userId={avatar.userId}
						fullName={avatar.name}
						trigger="click"
						position="right-start"
						offset={[0, 8]}
						ariaLabel={avatar.name}
						fullWidthTrigger
					>
						{row}
					</ProfileCardWrapper>
				</div>
			);
		},
		[showProfileCards],
	);

	const overrides = useMemo<AvatarGroupOverrides>(
		() => ({ AvatarGroupItem: { render: renderOverflowItem } }),
		[renderOverflowItem],
	);

	return (
		<>
			{approverData.length > 0 && (
				<Inline space="space.200" xcss={styles.avatarGroupRow}>
					<AvatarGroup
						data={approverData}
						maxCount={maxCount}
						{...(showProfileCards
							? { avatar: renderAvatar, isTooltipDisabled: true, overrides }
							: {})}
					/>
					{approverData.length === 1 ? (
						<FormattedMessage
							{...messages.singleUserMessage}
							values={{ name: approverData[0]?.name }}
						/>
					) : (
						<FormattedMessage
							{...messages.multipleUsersMessage}
							values={{
								name: approverData[0]?.name,
								count: approverData.length - 1,
							}}
						/>
					)}
				</Inline>
			)}
		</>
	);
};
